import React, { useState, useEffect } from 'react';
import { Modal, ModalHeader, ModalBody, ModalFooter, Row, Col, FormGroup, Label, Button, Input, Alert } from 'reactstrap';
import { ToastNotification } from '../Lessons/CourseReport/CourseReportComponents';
import {  addData, updateData } from '../../lib/studentchecklist';
import { ChekListTypeSelect } from '../Common/FormSelects';

const URGENCY = {
    Must: 'Must',
    Should: 'Should',
    BetterHave: 'Better Have'
};

const months = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];

const ChecklistModal = ({ isOpen, toggle, studentId, editingItem, onSave }) => {
    const [startMonth, setStartMonth] = useState('');
    const [endMonth, setEndMonth] = useState('');
    const [urgency, setUrgency] = useState('Must');
    const [taskId, setTaskId] = useState('');
    const [tDate, setTDate] = useState('');
    const [isDone, setIsDone] = useState(false);
    const [isProgress, setIsProgress] = useState(false);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState('');
    const [toastMessage, setToastMessage] = useState('');
    const [showToast, setShowToast] = useState(false);

    const showToastMessage = (message) => {
        setToastMessage(message);
        setShowToast(true);
        setTimeout(() => setShowToast(false), 3000);
    }

    const isEdit = !!editingItem;

    useEffect(() => {
        if (isOpen) {
            if (isEdit) {
                setStartMonth(editingItem.startMonth || '');
                setEndMonth(editingItem.endMonth || '');
                setUrgency(editingItem.urgency || 'Must');
                setTaskId(editingItem.taskId || '');
                setTDate(editingItem.tDate || '');
                setIsDone(!!editingItem.isDone);
                setIsProgress(!!editingItem.isProgress);
            } else {
                setStartMonth('');
                setEndMonth('');
                setUrgency('Must');
                setTaskId('');
                setTDate('');
                setIsDone(false);
                setIsProgress(false);
            }
            setError('');
        }
    }, [isOpen, editingItem]);

    const handleSubmit = async (e) => {
        e.preventDefault();
        setLoading(true);
        setError('');

        try {
            const payload = {
                studentId,
                startMonth,
                endMonth,
                urgency,
                taskId: taskId === '' || taskId === 0 ? null : taskId,
                tDate,
                isDone,
                isProgress
            };

            if (isEdit) {
                await updateData({ ...payload, id: editingItem.id });
            } else {
                await addData(payload);
            }
            
            if (onSave) await onSave();
            showToastMessage(isEdit ? 'Checklist item updated successfully!' : 'Checklist item added successfully!');
            setTimeout(() => toggle(), 2000);
        } catch (err) {
            console.error('Error saving checklist item:', err);
            setError('Failed to save checklist item');
        } finally {
            setLoading(false);
        }
    };

    return (
        <Modal isOpen={isOpen} toggle={toggle} size="lg">
            <ModalHeader toggle={toggle}>
                {isEdit ? 'Update Checklist Item' : 'Add Checklist Item'}
            </ModalHeader>
            <ToastNotification show={showToast} message={toastMessage} onClose={() => setShowToast(false)} />
            <ModalBody>
                {error && <Alert color="danger">{error}</Alert>}
                <form onSubmit={handleSubmit} id="checklist-form">
                    <Row>
                        <Col lg="6">
                            <FormGroup>
                                <Label className="form-control-label">Time Frame</Label>
                                <div className="d-flex align-items-center gap-2">
                                    <Input type="select" value={startMonth} onChange={(e) => setStartMonth(e.target.value)} required disabled={loading}>
                                        <option value="">Start Month</option>
                                        {months.map((month, index) => (
                                            <option key={index} value={month}>{month}</option>
                                        ))}
                                    </Input>
                                    <span>-</span>
                                    <Input type="select" value={endMonth} onChange={(e) => setEndMonth(e.target.value)} required disabled={loading}>
                                        <option value="">End Month</option>
                                        {months.map((month, index) => (
                                            <option key={index} value={month}>{month}</option>
                                        ))}
                                    </Input>
                                </div>
                            </FormGroup>
                        </Col>
                        <Col lg="6">
                            <FormGroup>
                                <Label className="form-control-label">Urgency</Label>
                                <Input type="select" value={urgency} onChange={(e) => setUrgency(e.target.value)} required disabled={loading}>
                                    {Object.keys(URGENCY).map((key) => (
                                        <option key={key} value={key}>{URGENCY[key]}</option>
                                    ))}
                                </Input>
                            </FormGroup>
                        </Col>
                    </Row>

                    <Row>
                        <Col lg="6">
                            <FormGroup>
                                <Label className="form-control-label">Task</Label>
                                <ChekListTypeSelect value={taskId} setValue={setTaskId} />
                            </FormGroup>
                        </Col>
                        <Col lg="6">
                            <FormGroup>
                                <Label className="form-control-label">Date</Label>
                                <Input type="date" value={tDate} onChange={(e) => setTDate(e.target.value)} required disabled={loading} />
                            </FormGroup>
                        </Col>
                    </Row>

                    <Row>
                        <Col md="6">
                            <FormGroup>
                                <Label className="form-control-label">Done</Label>
                                <div className="form-check form-switch">
                                    <Input type="checkbox" checked={isDone} onChange={(e) => setIsDone(e.target.checked)} disabled={loading} />
                                </div>
                            </FormGroup>
                        </Col>
                        <Col lg="6">
                            <FormGroup>
                                <Label className="form-control-label">In Progress</Label>
                                <div className="form-check form-switch">
                                    <Input type="checkbox" checked={isProgress} onChange={(e) => setIsProgress(e.target.checked)} disabled={loading} />
                                </div>
                            </FormGroup>
                        </Col>
                    </Row>
                </form>
            </ModalBody>
            <ModalFooter>
                <Button color="primary" type="submit" form="checklist-form" disabled={loading}>
                    {loading ? 'Saving...' : isEdit ? 'Update Item' : 'Add Item'}
                </Button>
                <Button color="secondary" onClick={toggle} disabled={loading}>
                    Cancel
                </Button>
            </ModalFooter>
        </Modal>
    );
};

export default ChecklistModal;
